import { useState, useEffect, useMemo } from 'react';
import { LabelTemplate, LabelCell, PlacedBlock, SocketConnection, TextAnnotation } from '@/app/types/cad';
import { X, Plus, Trash2, ChevronDown, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Autocomplete } from '@/app/components/ui/autocomplete';

interface LabelDesignerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: LabelTemplate; // Für Edit-Modus
  onSave: (template: LabelTemplate) => void;
  blocks?: PlacedBlock[]; // Für Attribut-Extraktion
  connections?: SocketConnection[]; // Für Attribut-Extraktion
  textAnnotations?: TextAnnotation[]; // Für Attribut-Extraktion
}

export function LabelDesignerDialog({
  open,
  onOpenChange,
  template,
  onSave,
  blocks = [],
  connections = [],
  textAnnotations = []
}: LabelDesignerDialogProps) {
  const [name, setName] = useState('Neue Beschriftung');
  const [columns, setColumns] = useState(1);
  const [rows, setRows] = useState<LabelCell[][]>([[{ type: 'text', content: '' }]]);
  const [cellWidth, setCellWidth] = useState(120);
  const [cellHeight, setCellHeight] = useState(30);
  const [fontSize, setFontSize] = useState(12);
  const [borderColor, setBorderColor] = useState('#000000');
  const [textColor, setTextColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [borderWidth, setBorderWidth] = useState(1);
  const [leaderLineWidth, setLeaderLineWidth] = useState(1);

  // Extrahiere alle verfügbaren Attribute aus dem Projekt
  const availableAttributes = useMemo(() => {
    const attributeSet = new Set<string>();
    
    // Standard-Attribute für Blocks
    attributeSet.add('id');
    attributeSet.add('blockId');
    attributeSet.add('name');
    attributeSet.add('blockName');
    attributeSet.add('version');
    attributeSet.add('system');
    attributeSet.add('systemId');
    attributeSet.add('position');
    attributeSet.add('u');
    attributeSet.add('v');
    attributeSet.add('rotation');
    
    // Standard-Attribute für Connections
    attributeSet.add('connectionId');
    attributeSet.add('dn');
    attributeSet.add('DN');
    attributeSet.add('diameter');
    attributeSet.add('connectedCount');
    
    // Standard-Attribute für Text-Annotationen
    attributeSet.add('text');
    attributeSet.add('fontSize');
    attributeSet.add('color');
    
    // Custom Attribute aus Blocks
    blocks.forEach(block => {
      if (block.blockData.attributes) {
        block.blockData.attributes.forEach(attr => {
          attributeSet.add(attr.name);
          // Auch verschiedene Case-Varianten hinzufügen
          attributeSet.add(attr.name.toLowerCase());
          attributeSet.add(attr.name.toUpperCase());
          const capitalized = attr.name.charAt(0).toUpperCase() + attr.name.slice(1);
          attributeSet.add(capitalized);
        });
      }
    });
    
    // Custom Attribute aus Connections
    connections.forEach(connection => {
      if (connection.attributes) {
        connection.attributes.forEach(attr => {
          attributeSet.add(attr.name);
          attributeSet.add(attr.name.toLowerCase());
          attributeSet.add(attr.name.toUpperCase());
          const capitalized = attr.name.charAt(0).toUpperCase() + attr.name.slice(1);
          attributeSet.add(capitalized);
        });
      }
    });
    
    // Konvertiere zu Array und sortiere alphabetisch
    return Array.from(attributeSet).sort((a, b) => a.localeCompare(b));
  }, [blocks, connections]);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setColumns(template.columns);
      setRows(template.rows);
      setCellWidth(template.cellWidth);
      setCellHeight(template.cellHeight);
      setFontSize(template.fontSize);
      setBorderColor(template.borderColor);
      setTextColor(template.textColor);
      setBackgroundColor(template.backgroundColor);
      setBorderWidth(template.borderWidth);
      setLeaderLineWidth(template.leaderLineWidth || 1);
    } else {
      // Reset für neues Template
      setName('Neue Beschriftung');
      setColumns(1);
      setRows([[{ type: 'text', content: '' }]]);
      setCellWidth(120);
      setCellHeight(30);
      setFontSize(12);
      setBorderColor('#000000');
      setTextColor('#000000');
      setBackgroundColor('#ffffff');
      setBorderWidth(1);
      setLeaderLineWidth(1);
    }
  }, [template, open]);

  const handleAddRow = () => {
    const newRow: LabelCell[] = Array(columns).fill(null).map(() => ({
      type: 'text',
      content: ''
    }));
    setRows([...rows, newRow]);
  };

  const handleRemoveRow = (rowIndex: number) => {
    if (rows.length > 1) {
      setRows(rows.filter((_, idx) => idx !== rowIndex));
    }
  };

  const handleCellChange = (rowIndex: number, colIndex: number, field: keyof LabelCell, value: string) => {
    const newRows = [...rows];
    newRows[rowIndex][colIndex] = {
      ...newRows[rowIndex][colIndex],
      [field]: value
    };
    setRows(newRows);
  };

  const handleColumnsChange = (newColumns: number) => {
    if (newColumns < 1) return;
    
    setColumns(newColumns);
    
    // Passe bestehende Zeilen an
    const newRows = rows.map(row => {
      if (row.length < newColumns) {
        // Füge Zellen hinzu
        const cellsToAdd = newColumns - row.length;
        return [...row, ...Array(cellsToAdd).fill(null).map(() => ({ type: 'text' as const, content: '' }))];
      } else if (row.length > newColumns) {
        // Entferne Zellen
        return row.slice(0, newColumns);
      }
      return row;
    });
    
    setRows(newRows);
  };

  const handleSave = () => {
    const newTemplate: LabelTemplate = {
      id: template?.id || `label-${Date.now()}`,
      name,
      columns,
      rows,
      cellWidth,
      cellHeight,
      fontSize,
      borderColor,
      textColor,
      backgroundColor,
      borderWidth,
      leaderLineWidth,
      created: template?.created || new Date().toISOString(),
      modified: new Date().toISOString()
    };
    
    onSave(newTemplate);
    onOpenChange(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Beschriftungs-Designer</h2>
          <button
            onClick={() => onOpenChange(false)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. DN-Info, Bauteil-Details"
            />
          </div>

          {/* Tabellen-Struktur */}
          <div className="space-y-4">
            <h3 className="font-medium">Tabellen-Struktur</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Spalten</label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  value={columns}
                  onChange={(e) => handleColumnsChange(parseInt(e.target.value) || 1)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Zeilen: {rows.length}</label>
                <Button onClick={handleAddRow} size="sm">
                  <Plus className="w-4 h-4 mr-1" />
                  Zeile hinzufügen
                </Button>
              </div>
            </div>

            {/* Tabellen-Editor */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="space-y-2">
                {rows.map((row, rowIndex) => (
                  <div key={rowIndex} className="flex items-start gap-2">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium w-16">Zeile {rowIndex + 1}:</span>
                        {rows.length > 1 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveRow(rowIndex)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
                        {row.map((cell, colIndex) => (
                          <div key={colIndex} className="space-y-1">
                            <div className="text-xs font-medium text-gray-600 mb-1">Typ</div>
                            <select
                              value={cell.type}
                              onChange={(e) => handleCellChange(rowIndex, colIndex, 'type', e.target.value)}
                              className="w-full px-2 py-1 text-sm border rounded"
                            >
                              <option value="text">Freitext</option>
                              <option value="attribute">Attribut</option>
                            </select>
                            
                            <div className="text-xs font-medium text-gray-600 mb-1">Inhalt</div>
                            {cell.type === 'attribute' ? (
                              <Autocomplete
                                value={cell.content}
                                onChange={(value) => handleCellChange(rowIndex, colIndex, 'content', value)}
                                options={availableAttributes}
                                placeholder="Attribut wählen..."
                                className="text-sm"
                              />
                            ) : (
                              <Input
                                value={cell.content}
                                onChange={(e) => handleCellChange(rowIndex, colIndex, 'content', e.target.value)}
                                placeholder="Text eingeben"
                                className="text-sm"
                              />
                            )}
                            
                            <div className="text-xs font-medium text-gray-600 mb-1">Ausrichtung</div>
                            <div className="flex gap-1 border rounded p-1">
                              <button
                                type="button"
                                onClick={() => handleCellChange(rowIndex, colIndex, 'align', 'left')}
                                className={`flex-1 p-1.5 rounded flex items-center justify-center transition-colors ${
                                  (cell.align || 'center') === 'left' 
                                    ? 'bg-blue-500 text-white' 
                                    : 'hover:bg-gray-100'
                                }`}
                                title="Linksbündig"
                              >
                                <AlignLeft className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleCellChange(rowIndex, colIndex, 'align', 'center')}
                                className={`flex-1 p-1.5 rounded flex items-center justify-center transition-colors ${
                                  (cell.align || 'center') === 'center' 
                                    ? 'bg-blue-500 text-white' 
                                    : 'hover:bg-gray-100'
                                }`}
                                title="Zentriert"
                              >
                                <AlignCenter className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleCellChange(rowIndex, colIndex, 'align', 'right')}
                                className={`flex-1 p-1.5 rounded flex items-center justify-center transition-colors ${
                                  (cell.align || 'center') === 'right' 
                                    ? 'bg-blue-500 text-white' 
                                    : 'hover:bg-gray-100'
                                }`}
                                title="Rechtsbündig"
                              >
                                <AlignRight className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Formatierung */}
          <div className="space-y-4">
            <h3 className="font-medium">Formatierung</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Zellbreite (px)</label>
                <Input
                  type="number"
                  min="50"
                  max="300"
                  value={cellWidth}
                  onChange={(e) => setCellWidth(parseInt(e.target.value) || 120)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Zellhöhe (px)</label>
                <Input
                  type="number"
                  min="20"
                  max="100"
                  value={cellHeight}
                  onChange={(e) => setCellHeight(parseInt(e.target.value) || 30)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Schriftgröße (px)</label>
                <Input
                  type="number"
                  min="8"
                  max="24"
                  value={fontSize}
                  onChange={(e) => setFontSize(parseInt(e.target.value) || 12)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Rahmendicke (px)</label>
                <Input
                  type="number"
                  min="0"
                  max="5"
                  value={borderWidth}
                  onChange={(e) => setBorderWidth(parseInt(e.target.value) || 1)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Verbindungslinie - Linienstärke</label>
                <Input
                  type="number"
                  min="0.5"
                  max="10"
                  step="0.5"
                  value={leaderLineWidth}
                  onChange={(e) => setLeaderLineWidth(parseFloat(e.target.value) || 1)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Standard-Linienstärke für alle mit diesem Template erstellten Labels
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Rahmenfarbe</label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={borderColor}
                    onChange={(e) => setBorderColor(e.target.value)}
                    className="w-12 h-8 p-1"
                  />
                  <Input
                    type="text"
                    value={borderColor}
                    onChange={(e) => setBorderColor(e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Textfarbe</label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                    className="w-12 h-8 p-1"
                  />
                  <Input
                    type="text"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Hintergrund</label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="w-12 h-8 p-1"
                  />
                  <Input
                    type="text"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Vorschau */}
          <div className="space-y-2">
            <h3 className="font-medium">Vorschau</h3>
            <div className="border rounded-lg p-4 bg-gray-50 flex items-center justify-center">
              <table style={{ borderCollapse: 'collapse' }}>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, colIndex) => (
                        <td
                          key={colIndex}
                          style={{
                            width: `${cellWidth}px`,
                            height: `${cellHeight}px`,
                            border: `${borderWidth}px solid ${borderColor}`,
                            backgroundColor,
                            color: textColor,
                            fontSize: `${fontSize}px`,
                            padding: '4px 8px',
                            textAlign: cell.align || 'center',
                            verticalAlign: 'middle'
                          }}
                        >
                          {cell.type === 'attribute' 
                            ? `{${cell.content || 'attribut'}}` 
                            : (cell.content || 'Beispieltext')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={handleSave}>
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
}